'use client';

import { c as _c } from "react/compiler-runtime";
import { useAuth, useFollow, useGetFollowsInfinite, usePrefetchGetUser, useUnfollow } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import type React from 'react';
import { useState } from 'react';
import { UserAvatar } from '@/components/images/user-avatar';
import { UserIdentity } from '@/components/profile/user-identity';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Skeleton } from '@/components/ui/app-skeleton';
import { InfiniteList } from '@/components/ui/infinite-list';
import { MotionAnimatedAutoHeight } from '@/components/ui/motion-animated-auto-height';
import { SegmentedControl, type SegmentedControlItem } from '@/components/ui/segmented-control';
import { SilkSheet } from '@/components/ui/silk-sheet';
import { TOAST_IDS } from '@/config/toast-ids';
import { useLiveListData } from '@/hooks/sdk/use-live-list-data';
import { Link } from '@/i18n/navigation';
import { getProfileHref } from '@/lib/profile/profile-href';
import { FOLLOWS_LIST_FIELDS, PORTFOLIO_USER_PROFILE_FIELDS } from '@/lib/sdk/projections';
import { showToast } from '@/lib/ui/toast';
interface FollowersDialogProps {
  userId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultTab?: 'followers' | 'following';
}

/**
 * Local row type derived from `GetFollowsRow.user` — the v2 follows feed
 * carries a nested `user` object; the dialog rows project it into the
 * `<UserAvatar>` / `<UserIdentity>` shape.
 */
interface FollowRowUser {
  readonly userId: string | null;
  readonly walletAddress: string;
  readonly displayName: string | null;
  readonly handle: string | null;
  readonly avatarUrl: string | null;
  readonly avatarNsfw?: boolean;
  readonly avatarPreferences?: {
    readonly rasterise: boolean;
  };
}

// ---------------------------------------------------------------------------
// Following tab item — always shows Unfollow, removes on click
// ---------------------------------------------------------------------------

function FollowingListItem(t0) {
  const $ = _c(39);
  const {
    user,
    onNavigate,
    onUnfollow
  } = t0;
  const t = useTranslations("profile.follow");
  const prefetchUser = usePrefetchGetUser();
  const [isHovered, setIsHovered] = useState(false);
  const targetId = user.userId ?? user.walletAddress;
  let t1;
  if ($[0] !== prefetchUser || $[1] !== targetId) {
    t1 = function prefetch() {
      prefetchUser({
        identifier: targetId,
        fields: [...PORTFOLIO_USER_PROFILE_FIELDS]
      });
    };
    $[0] = prefetchUser;
    $[1] = targetId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const prefetch = t1;
  let t2;
  if ($[3] !== targetId) {
    t2 = getProfileHref(targetId);
    $[3] = targetId;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const t3 = user.avatarNsfw ?? false;
  const t4 = user.avatarPreferences?.rasterise ?? false;
  let t5;
  if ($[5] !== t3 || $[6] !== t4 || $[7] !== targetId || $[8] !== user.avatarUrl || $[9] !== user.displayName) {
    t5 = <UserAvatar size="md" userId={targetId} displayName={user.displayName} avatarUrl={user.avatarUrl} nsfw={t3} rasterise={t4} className="shrink-0" />;
    $[5] = t3;
    $[6] = t4;
    $[7] = targetId;
    $[8] = user.avatarUrl;
    $[9] = user.displayName;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const t6 = user.avatarUrl ?? null;
  const t7 = user.avatarNsfw === true;
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = [];
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== t6 || $[13] !== t7 || $[14] !== targetId || $[15] !== user.displayName || $[16] !== user.handle) {
    t9 = <UserIdentity user={{
      userId: targetId,
      displayName: user.displayName,
      handle: user.handle,
      avatarUrl: t6,
      avatarNsfw: t7,
      connectedProviders: t8
    }} size="sm" />;
    $[12] = t6;
    $[13] = t7;
    $[14] = targetId;
    $[15] = user.displayName;
    $[16] = user.handle;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== onNavigate || $[19] !== prefetch || $[20] !== t2 || $[21] !== t5 || $[22] !== t9) {
    t10 = <Link href={t2} onClick={onNavigate} onMouseEnter={prefetch} onFocus={prefetch} onPointerDown={prefetch} className="flex min-w-0 grow items-center gap-3 text-left transition-colors hover:opacity-80">{t5}{t9}</Link>;
    $[18] = onNavigate;
    $[19] = prefetch;
    $[20] = t2;
    $[21] = t5;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  const t11 = isHovered ? "destructive" : "secondary";
  let t12;
  if ($[24] !== onUnfollow || $[25] !== targetId) {
    t12 = e => {
      e.stopPropagation();
      onUnfollow(targetId);
    };
    $[24] = onUnfollow;
    $[25] = targetId;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  let t14;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => {
      setIsHovered(true);
    };
    t14 = () => {
      setIsHovered(false);
    };
    $[27] = t13;
    $[28] = t14;
  } else {
    t13 = $[27];
    t14 = $[28];
  }
  let t15;
  if ($[29] !== isHovered || $[30] !== t) {
    t15 = isHovered ? t("unfollow_button") : t("following_button");
    $[29] = isHovered;
    $[30] = t;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== t11 || $[33] !== t12 || $[34] !== t15) {
    t16 = <AnimatedButton variant={t11} size="sm" className="shrink-0" onClick={t12} onPointerEnter={t13} onPointerLeave={t14}>{t15}</AnimatedButton>;
    $[32] = t11;
    $[33] = t12;
    $[34] = t15;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== t10 || $[37] !== t16) {
    t17 = <div className="flex w-full items-center gap-3 rounded-card px-3 py-2">{t10}{t16}</div>;
    $[36] = t10;
    $[37] = t16;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  return t17;
}

// ---------------------------------------------------------------------------
// Followers tab item — shows Follow/Following based on whether you follow back
// ---------------------------------------------------------------------------

function FollowerListItem(t0) {
  const $ = _c(47);
  const {
    user,
    isFollowedBack,
    onNavigate,
    onFollow,
    onUnfollow
  } = t0;
  const t = useTranslations("profile.follow");
  const prefetchUser = usePrefetchGetUser();
  const [isHovered, setIsHovered] = useState(false);
  const targetId = user.userId ?? user.walletAddress;
  let t1;
  if ($[0] !== prefetchUser || $[1] !== targetId) {
    t1 = function prefetch() {
      prefetchUser({
        identifier: targetId,
        fields: [...PORTFOLIO_USER_PROFILE_FIELDS]
      });
    };
    $[0] = prefetchUser;
    $[1] = targetId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const prefetch = t1;
  let t2;
  if ($[3] !== isFollowedBack || $[4] !== isHovered) {
    t2 = function getVariant() {
      if (!isFollowedBack) {
        return "primary";
      }
      if (isHovered) {
        return "destructive";
      }
      return "secondary";
    };
    $[3] = isFollowedBack;
    $[4] = isHovered;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const getVariant = t2;
  let t3;
  if ($[6] !== isFollowedBack || $[7] !== isHovered || $[8] !== t) {
    t3 = function getLabel() {
      if (!isFollowedBack) {
        return t("follow_button");
      }
      if (isHovered) {
        return t("unfollow_button");
      }
      return t("following_button");
    };
    $[6] = isFollowedBack;
    $[7] = isHovered;
    $[8] = t;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const getLabel = t3;
  let t4;
  if ($[10] !== targetId) {
    t4 = getProfileHref(targetId);
    $[10] = targetId;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const t5 = user.avatarNsfw ?? false;
  const t6 = user.avatarPreferences?.rasterise ?? false;
  let t7;
  if ($[12] !== t5 || $[13] !== t6 || $[14] !== targetId || $[15] !== user.avatarUrl || $[16] !== user.displayName) {
    t7 = <UserAvatar size="md" userId={targetId} displayName={user.displayName} avatarUrl={user.avatarUrl} nsfw={t5} rasterise={t6} className="shrink-0" />;
    $[12] = t5;
    $[13] = t6;
    $[14] = targetId;
    $[15] = user.avatarUrl;
    $[16] = user.displayName;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const t8 = user.avatarUrl ?? null;
  const t9 = user.avatarNsfw === true;
  let t10;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = [];
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t8 || $[20] !== t9 || $[21] !== targetId || $[22] !== user.displayName || $[23] !== user.handle) {
    t11 = <UserIdentity user={{
      userId: targetId,
      displayName: user.displayName,
      handle: user.handle,
      avatarUrl: t8,
      avatarNsfw: t9,
      connectedProviders: t10
    }} size="sm" />;
    $[19] = t8;
    $[20] = t9;
    $[21] = targetId;
    $[22] = user.displayName;
    $[23] = user.handle;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== onNavigate || $[26] !== prefetch || $[27] !== t11 || $[28] !== t4 || $[29] !== t7) {
    t12 = <Link href={t4} onClick={onNavigate} onMouseEnter={prefetch} onFocus={prefetch} onPointerDown={prefetch} className="flex min-w-0 grow items-center gap-3 text-left transition-colors hover:opacity-80">{t7}{t11}</Link>;
    $[25] = onNavigate;
    $[26] = prefetch;
    $[27] = t11;
    $[28] = t4;
    $[29] = t7;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  const t13 = getVariant();
  let t14;
  if ($[31] !== isFollowedBack || $[32] !== onFollow || $[33] !== onUnfollow || $[34] !== targetId) {
    t14 = e => {
      e.stopPropagation();
      if (isFollowedBack) {
        onUnfollow(targetId);
      } else {
        onFollow(targetId);
      }
    };
    $[31] = isFollowedBack;
    $[32] = onFollow;
    $[33] = onUnfollow;
    $[34] = targetId;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  let t16;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => {
      setIsHovered(true);
    };
    t16 = () => {
      setIsHovered(false);
    };
    $[36] = t15;
    $[37] = t16;
  } else {
    t15 = $[36];
    t16 = $[37];
  }
  let t17;
  if ($[38] !== getLabel) {
    t17 = getLabel();
    $[38] = getLabel;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== t13 || $[41] !== t14 || $[42] !== t17) {
    t18 = <AnimatedButton variant={t13} size="sm" className="shrink-0" onClick={t14} onPointerEnter={t15} onPointerLeave={t16}>{t17}</AnimatedButton>;
    $[40] = t13;
    $[41] = t14;
    $[42] = t17;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  let t19;
  if ($[44] !== t12 || $[45] !== t18) {
    t19 = <div className="flex w-full items-center gap-3 rounded-card px-3 py-2">{t12}{t18}</div>;
    $[44] = t12;
    $[45] = t18;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  return t19;
}

// ---------------------------------------------------------------------------
// Skeleton list
// ---------------------------------------------------------------------------

function FollowListSkeleton(t0) {
  const $ = _c(3);
  const {
    keyPrefix
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["a", "b", "c"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== keyPrefix) {
    t2 = <div className="flex flex-col gap-2 py-2">{t1.map(key => <div key={`skeleton-${keyPrefix}-${key}`} className="flex items-center gap-3 px-3 py-2"><Skeleton className="size-9 rounded-full" /><Skeleton className="h-4 w-32" /></div>)}</div>;
    $[1] = keyPrefix;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}

// ---------------------------------------------------------------------------
// Following list — with unfollow that removes from list
// ---------------------------------------------------------------------------

function FollowingList(t0) {
  const $ = _c(22);
  const {
    userId,
    onNavigate
  } = t0;
  const t = useTranslations("profile.follow");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [...FOLLOWS_LIST_FIELDS];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== userId) {
    t2 = {
      userId,
      kind: "following",
      fields: t1
    };
    $[1] = userId;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const query = useGetFollowsInfinite(t2);
  const items = query.rows;
  const {
    isPending,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage
  } = query;
  const unfollow = useUnfollow();
  let t3;
  if ($[3] !== t || $[4] !== unfollow) {
    t3 = function handleUnfollow(targetId) {
      unfollow.mutate({
        userId: targetId
      });
      showToast({
        id: TOAST_IDS.UNFOLLOW_ACTION,
        message: t("unfollow_success"),
        variant: "success"
      });
    };
    $[3] = t;
    $[4] = unfollow;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const handleUnfollow = t3;
  let t4;
  if ($[6] !== handleUnfollow || $[7] !== onNavigate) {
    t4 = item_0 => <FollowingListItem user={item_0.user} onNavigate={onNavigate} onUnfollow={handleUnfollow} />;
    $[6] = handleUnfollow;
    $[7] = onNavigate;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const t5 = hasNextPage === true;
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <FollowListSkeleton keyPrefix="following" />;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t) {
    t7 = t("empty_following");
    $[10] = t;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t7) {
    t8 = <p className="py-8 text-center text-caption text-foreground-secondary">{t7}</p>;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== fetchNextPage || $[15] !== isFetchingNextPage || $[16] !== isPending || $[17] !== items || $[18] !== t4 || $[19] !== t5 || $[20] !== t8) {
    t9 = <InfiniteList items={items} getItemKey={_temp} renderItem={t4} hasNextPage={t5} isFetchingNextPage={isFetchingNextPage} fetchNextPage={fetchNextPage} isInitialLoading={isPending} loadingPlaceholder={t6} emptyState={t8} listClassName="flex flex-col gap-1 py-2" maxHeight={320} />;
    $[14] = fetchNextPage;
    $[15] = isFetchingNextPage;
    $[16] = isPending;
    $[17] = items;
    $[18] = t4;
    $[19] = t5;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  return t9;
}

// ---------------------------------------------------------------------------
// Followers list — with follow-back / unfollow buttons
// ---------------------------------------------------------------------------
function _temp(item) {
  return item.user.userId ?? item.user.walletAddress;
}
function FollowersList(t0) {
  const $ = _c(32);
  const {
    userId,
    onNavigate
  } = t0;
  const t = useTranslations("profile.follow");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [...FOLLOWS_LIST_FIELDS];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== userId) {
    t2 = {
      userId,
      kind: "followers",
      fields: t1
    };
    $[1] = userId;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const followers = useGetFollowsInfinite(t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [...FOLLOWS_LIST_FIELDS];
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== userId) {
    t4 = {
      userId,
      kind: "following",
      fields: t3
    };
    $[4] = userId;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const following = useGetFollowsInfinite(t4);
  const follow = useFollow();
  const unfollow = useUnfollow();
  const items = useLiveListData(followers.queryKey);
  const followingRows = following.rows;
  let t5;
  if ($[6] !== followingRows) {
    t5 = new Set(followingRows.map(_temp2));
    $[6] = followingRows;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const followedBackSet = t5;
  let t6;
  if ($[8] !== follow || $[9] !== t) {
    t6 = function handleFollow(targetId) {
      follow.mutate({
        userId: targetId
      });
      showToast({
        id: TOAST_IDS.FOLLOW_ACTION,
        message: t("follow_success"),
        variant: "success"
      });
    };
    $[8] = follow;
    $[9] = t;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const handleFollow = t6;
  let t7;
  if ($[11] !== t || $[12] !== unfollow) {
    t7 = function handleUnfollow(targetId_0) {
      unfollow.mutate({
        userId: targetId_0
      });
      showToast({
        id: TOAST_IDS.UNFOLLOW_ACTION,
        message: t("unfollow_success"),
        variant: "success"
      });
    };
    $[11] = t;
    $[12] = unfollow;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const handleUnfollow = t7;
  let t8;
  if ($[14] !== followedBackSet || $[15] !== handleFollow || $[16] !== handleUnfollow || $[17] !== onNavigate) {
    t8 = item_0 => {
      const rowId = item_0.user.userId ?? item_0.user.walletAddress;
      return <FollowerListItem user={item_0.user} isFollowedBack={followedBackSet.has(rowId)} onNavigate={onNavigate} onFollow={handleFollow} onUnfollow={handleUnfollow} />;
    };
    $[14] = followedBackSet;
    $[15] = handleFollow;
    $[16] = handleUnfollow;
    $[17] = onNavigate;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const t9 = followers.hasNextPage === true;
  const t10 = followers.isFetchingNextPage;
  const t11 = followers.fetchNextPage;
  const t12 = followers.isPending;
  let t13;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <FollowListSkeleton keyPrefix="followers" />;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] !== t) {
    t14 = t("empty_followers");
    $[20] = t;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] !== t14) {
    t15 = <p className="py-8 text-center text-caption text-foreground-secondary">{t14}</p>;
    $[22] = t14;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  let t16;
  if ($[24] !== followers.fetchNextPage || $[25] !== followers.isFetchingNextPage || $[26] !== followers.isPending || $[27] !== items || $[28] !== t15 || $[29] !== t8 || $[30] !== t9) {
    t16 = <InfiniteList items={items} getItemKey={_temp3} renderItem={t8} hasNextPage={t9} isFetchingNextPage={t10} fetchNextPage={t11} isInitialLoading={t12} loadingPlaceholder={t13} emptyState={t15} listClassName="flex flex-col gap-1 py-2" maxHeight={320} />;
    $[24] = followers.fetchNextPage;
    $[25] = followers.isFetchingNextPage;
    $[26] = followers.isPending;
    $[27] = items;
    $[28] = t15;
    $[29] = t8;
    $[30] = t9;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  return t16;
}

// ---------------------------------------------------------------------------
// Dialog
// ---------------------------------------------------------------------------
function _temp3(item) {
  return item.user.userId ?? item.user.walletAddress;
}
function _temp2(r) {
  return r.user.userId ?? r.user.walletAddress;
}
export function FollowersDialog(t0) {
  const $ = _c(37);
  const {
    userId,
    open,
    onOpenChange,
    defaultTab: t1
  } = t0;
  const defaultTab = t1 === undefined ? "followers" : t1;
  const t = useTranslations("profile.follow");
  const {
    isAuthenticated,
    user
  } = useAuth();
  const isOwn = isAuthenticated && user !== null && user.id === userId;
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setActiveTab(defaultTab);
    }
  }
  let t2;
  if ($[0] !== onOpenChange) {
    t2 = function handleClose() {
      onOpenChange(false);
    };
    $[0] = onOpenChange;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const handleClose = t2;
  let t3;
  if ($[2] !== t) {
    t3 = t("followers_tab");
    $[2] = t;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t3) {
    t4 = {
      key: "followers",
      label: t3
    };
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t) {
    t5 = t("following_tab");
    $[6] = t;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t5) {
    t6 = {
      key: "following",
      label: t5
    };
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t4 || $[11] !== t6) {
    t7 = [t4, t6];
    $[10] = t4;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const tabItems = t7;
  let t8;
  if ($[13] !== t) {
    t8 = t("followers_tab");
    $[13] = t;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = key => setActiveTab(key as 'followers' | 'following');
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== activeTab || $[17] !== tabItems) {
    t10 = <SegmentedControl variant="tab-surface" asTabs={true} items={tabItems} active={activeTab} onChange={t9} layoutId="followers-dialog-tabs-pill" fullWidth={true} className="rounded-pill bg-background-tertiary p-1" />;
    $[16] = activeTab;
    $[17] = tabItems;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== activeTab) {
    t11 = [activeTab];
    $[19] = activeTab;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== activeTab || $[22] !== handleClose || $[23] !== isOwn || $[24] !== userId) {
    t12 = activeTab === "followers" ? isOwn ? <FollowersList userId={userId} onNavigate={handleClose} /> : <SimpleUserList userId={userId} type="followers" onNavigate={handleClose} /> : isOwn ? <FollowingList userId={userId} onNavigate={handleClose} /> : <SimpleUserList userId={userId} type="following" onNavigate={handleClose} />;
    $[21] = activeTab;
    $[22] = handleClose;
    $[23] = isOwn;
    $[24] = userId;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t11 || $[27] !== t12) {
    t13 = <MotionAnimatedAutoHeight layoutDependencies={t11}>{t12}</MotionAnimatedAutoHeight>;
    $[26] = t11;
    $[27] = t12;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== t10 || $[30] !== t13) {
    t14 = <div className="flex flex-col gap-3">{t10}{t13}</div>;
    $[29] = t10;
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== onOpenChange || $[33] !== open || $[34] !== t14 || $[35] !== t8) {
    t15 = <SilkSheet open={open} onOpenChange={onOpenChange} variant="detached" size="sm" title={t8}>{t14}</SilkSheet>;
    $[32] = onOpenChange;
    $[33] = open;
    $[34] = t14;
    $[35] = t8;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  return t15;
}

// ---------------------------------------------------------------------------
// Simple list (no actions) — for viewing other people's profiles
// ---------------------------------------------------------------------------

function SimpleUserList(t0) {
  const $ = _c(23);
  const {
    userId,
    type,
    onNavigate
  } = t0;
  const t = useTranslations("profile.follow");
  const prefetchUser = usePrefetchGetUser();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [...FOLLOWS_LIST_FIELDS];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== type || $[2] !== userId) {
    t2 = {
      userId,
      kind: type,
      fields: t1
    };
    $[1] = type;
    $[2] = userId;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const query = useGetFollowsInfinite(t2);
  const items = useLiveListData(query.queryKey);
  const {
    isPending,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage
  } = query;
  let t3;
  if ($[4] !== onNavigate || $[5] !== prefetchUser) {
    t3 = item_0 => {
      const rowId = item_0.user.userId ?? item_0.user.walletAddress;
      const prefetch = () => {
        prefetchUser({
          identifier: rowId,
          fields: [...PORTFOLIO_USER_PROFILE_FIELDS]
        });
      };
      return <Link href={getProfileHref(rowId)} onClick={onNavigate} onMouseEnter={prefetch} onFocus={prefetch} onPointerDown={prefetch} className="flex w-full items-center gap-3 rounded-card px-3 py-2 text-left transition-colors hover:bg-background-tertiary/50"><UserAvatar size="md" userId={rowId} displayName={item_0.user.displayName} avatarUrl={item_0.user.avatarUrl} nsfw={item_0.user.avatarNsfw ?? false} rasterise={item_0.user.avatarPreferences?.rasterise ?? false} className="shrink-0" /><UserIdentity user={{
          userId: rowId,
          displayName: item_0.user.displayName,
          handle: item_0.user.handle,
          avatarUrl: item_0.user.avatarUrl ?? null,
          avatarNsfw: item_0.user.avatarNsfw === true,
          connectedProviders: []
        }} size="sm" /></Link>;
    };
    $[4] = onNavigate;
    $[5] = prefetchUser;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const t4 = hasNextPage === true;
  let t5;
  if ($[7] !== type) {
    t5 = <FollowListSkeleton keyPrefix={type} />;
    $[7] = type;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t || $[10] !== type) {
    t6 = type === "followers" ? t("empty_followers") : t("empty_following");
    $[9] = t;
    $[10] = type;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t6) {
    t7 = <p className="py-8 text-center text-caption text-foreground-secondary">{t6}</p>;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== fetchNextPage || $[15] !== isFetchingNextPage || $[16] !== isPending || $[17] !== items || $[18] !== t3 || $[19] !== t4 || $[20] !== t5 || $[21] !== t7) {
    t8 = <InfiniteList items={items} getItemKey={_temp4} renderItem={t3} hasNextPage={t4} isFetchingNextPage={isFetchingNextPage} fetchNextPage={fetchNextPage} isInitialLoading={isPending} loadingPlaceholder={t5} emptyState={t7} listClassName="flex flex-col gap-1 py-2" maxHeight={320} />;
    $[14] = fetchNextPage;
    $[15] = isFetchingNextPage;
    $[16] = isPending;
    $[17] = items;
    $[18] = t3;
    $[19] = t4;
    $[20] = t5;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  return t8;
}
function _temp4(item) {
  return item.user.userId ?? item.user.walletAddress;
}